import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useFRT } from 'pages/FRT/hooks';
import { Typography } from '@mui/material';
import Grid from '@mui/material/Grid';
import Breadcrumbs from 'components/atoms/Breadcrumbs';
import { ERouteLinks } from 'models/route';
import { Box, Stack } from '@mui/system';
import { StyledPageTitle } from 'theme/styles';
import { Loader } from 'components/atoms/Loader';
import { DetailsTemplate, EDetailsPageTypes } from 'components/templates/DetailsTemplate';
import { Accordion } from 'components/molecules/Accordion';
import { StyledDetailsWrapper } from 'pages/Firearms/RegisteredFirearmDeleteCommentPage/styles';
import { Table } from 'components/molecules/Table';
import {
  FRTSpecificationsTableColumns,
  isFRTLocal,
  mapFRTSpecificationToDataSource,
} from 'pages/FRT/helpers';
import { CardList, emptyFieldHolder } from 'components/molecules/CardList';
import { useThemeBreakpoint } from 'hooks/useThemeBreakpoint';
import Button from 'components/atoms/Button';
import { EButtonVariants } from 'constants/Buttons';
import { EIconTypes } from 'constants/Icons';
import { StyledButton } from 'pages/Firearms/ViewRegisteredFirearmPage/styles';
import { useAlert } from 'models/alertContext';
import { Alert } from 'components/atoms/Alert';
import { ActionTypes } from 'state/actions/alert';
import { FRTSpecificationsCard } from 'pages/FRT/FRTSpecificationsCard';
import { useTranslations } from 'hooks/useTranslations';
import { EPermission } from 'models/permissions';
import { usePermission } from 'hooks/usePermission';
import { getHtmlText } from 'components/organisms/HtmlSlate';
import ImagesGallery from 'components/organisms/ImagesGallery';
import { EModuleType } from 'components/organisms/ImagesGallery/helper';
import { findInDictionary } from 'helpers/dictionary';
import { useLang } from 'models/langContext';
import { useDictionary } from 'models/dictionaryContext';

const FRTDetailsPage = () => {
  const { frtId = '' } = useParams<{ id: string; frtId: string }>();
  const { hasPermissions } = usePermission();
  const [isFRTSpecificationsLoading, setIsFRTSpecificationsLoading] = useState<boolean>(false);
  const [firearmDetailsAllExpanded, setFirearmDetailsAllExpanded] = useState<boolean>(false);
  const {
    getFRTDetailsData,
    FRTDetails,
    getFRTSpecificationsData,
    FRTSpecifications,
    getFRTNotesData,
    FRTNotes,
  } = useFRT();
  const { isDesktop } = useThemeBreakpoint();
  const navigate = useNavigate();
  const { nextPageAlert, clearAlert } = useAlert();
  const { t } = useTranslations();
  const { selectedLanguage } = useLang();
  const {
    getMake,
    make,
    getModel,
    model,
    getFirearmAction,
    firearmAction,
    manufacturer,
    getManufacturer,
    manufacturerCountry,
    getManufacturerCountry,
    getCaliber,
    caliber,
    barrelType,
    getBarrelType,
  } = useDictionary();

  useEffect(() => {
    (async function initDictionaries() {
      getMake();
      getModel();
      getFirearmAction();
      getManufacturerCountry();
      getManufacturer();
      getCaliber();
      getBarrelType();
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedLanguage]);

  useEffect(() => {
    (async function getFRTSpecificationsDataInit() {
      try {
        setIsFRTSpecificationsLoading(true);
        await Promise.all([
          getFRTDetailsData(frtId),
          getFRTSpecificationsData(frtId),
          getFRTNotesData(frtId),
        ]);
      } catch (e) {
        console.error(e);
      }
      setIsFRTSpecificationsLoading(false);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [frtId]);

  useEffect(
    () => () => {
      clearAlert(ActionTypes.CLEAR_NEXT_PAGE_ALERT);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  const isLocal: boolean = isFRTLocal(frtId);

  let frtDetailsTitle = '';
  if (FRTDetails !== null) {
    const { makeUniqueId, modelUniqueId, actionUniqueId } = FRTDetails;
    frtDetailsTitle = `${findInDictionary(makeUniqueId, make)}, ${findInDictionary(modelUniqueId, model)}, ${findInDictionary(actionUniqueId, firearmAction)}`;
  }
  const calibres =
    FRTSpecifications.map((spec) => findInDictionary(spec.caliberUniqueId, caliber)).join(
      ', ',
    ) || '-';
  const frtNotes = FRTNotes.map((note) => (
    <Grid key={note.type} size={{ xs: 12 }}>
      <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
        {note.type}
      </Typography>
      <Typography variant="body2">{getHtmlText(note.description)}</Typography>
    </Grid>
  ));
  const handleToggleExpandAll = () => {
    setFirearmDetailsAllExpanded((prev) => !prev);
  };

  return (
    <div>
      <Breadcrumbs
        items={[
          {
            label: t('firearms.registeredFirearms.breadcrumbs.firearms'),
            route: ERouteLinks.Firearms,
          },
          {
            label: t('firearmsReferenceTable.firearmDetails.firearmDetails.header'),
            route: `${ERouteLinks.Firearms}?expand=frt`,
          },
          isLocal
            ? t('firearmsReferenceTable.editFirearm.viewLocalFirearm.header')
            : t('firearmsReferenceTable.editFirearm.viewFirearm.header'),
        ]}
      />
      <Box sx={{ mb: 2 }}>
        <StyledPageTitle variant="h4">
          {isLocal
            ? t('firearmsReferenceTable.editFirearm.viewLocalFirearm.header')
            : t('firearmsReferenceTable.editFirearm.viewFirearm.header')}
        </StyledPageTitle>
      </Box>

      {nextPageAlert && (
        <Box sx={{ mb: 2 }}>
          <Alert text={nextPageAlert.text} variant={nextPageAlert.variant} />
        </Box>
      )}

      <Grid container>
        <Grid size={{ xs: 12 }} sx={{ mb: 5 }}>
          {isFRTSpecificationsLoading ? (
            <Loader isLoading={isFRTSpecificationsLoading} />
          ) : (
            <DetailsTemplate
              title={frtDetailsTitle}
              deletePermissions={[EPermission.MANAGE_FRT_FIREARM]}
              editPermissions={[EPermission.MANAGE_FRT_FIREARM]}
              redirectToEditView={
                isLocal
                  ? () => navigate(ERouteLinks.EditFRT.replace(':frtId', frtId))
                  : undefined
              }
              pageType={isLocal ? EDetailsPageTypes.view : EDetailsPageTypes.frtView}
              hasLongTitle={true}
            >
              <Grid
                container
                sx={{
                  flexWrap: { xs: 'wrap', md: 'nowrap' },
                  justifyContent: 'center',
                  gap: '20px',
                }}
              >
                <Grid
                  sx={{
                    gap: '12px',
                    justifyContent: 'center',
                    alignContent: 'flex-start',
                    width: '270px',
                  }}
                >
                  <ImagesGallery
                    moduleType={EModuleType.FRT}
                    ownerId={frtId}
                    buttonId={'frt-add_photo'}
                    showButton={!!isLocal}
                    buttonPermission={EPermission.MANAGE_FRT_FIREARM}
                    onClickEdit={() =>
                      navigate(
                        `${ERouteLinks.FRTEditImages.replace(':frtId', frtId as string)}`,
                      )
                    }
                    onClickAdd={() =>
                      navigate(
                        `${ERouteLinks.FRTAddImages.replace(':frtId', frtId as string)}`,
                      )
                    }
                  />
                </Grid>
                <Grid
                  sx={{ width: 'calc(100% - 270px)', rowGap: 1, alignContent: 'flex-start' }}
                  size={{ xs: 4 }}
                >
                  <Stack>
                    <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                      {t('firearmsReferenceTable.editFirearm.calibres.label')}
                    </Typography>
                    <Typography variant="body2">{calibres}</Typography>
                  </Stack>
                </Grid>
              </Grid>
            </DetailsTemplate>
          )}
        </Grid>
        <Grid
          size={{ xs: 12 }}
          sx={{ display: 'flex', justifyContent: 'flex-end', mt: -2, mb: 1 }}
        >
          <StyledButton
            label={
              firearmDetailsAllExpanded
                ? t('firearmsReferenceTable.firearmDetails.collapseAll.link')
                : t('firearmsReferenceTable.firearmDetails.expandAll.link')
            }
            onClick={handleToggleExpandAll}
            variant={EButtonVariants.outlined}
          />
        </Grid>

        <Grid size={{ xs: 12 }}>
          <Accordion
            title={t('firearmsReferenceTable.editFirearm.general.accordion.name')}
            expanded={firearmDetailsAllExpanded}
          >
            {FRTDetails && (
              <Box sx={{ mb: 2 }}>
                <StyledDetailsWrapper container sx={{ rowGap: 3 }} columnSpacing={2}>
                  <Grid size={{ xs: 12, md: 6, lg: 4, xl: 3 }}>
                    <Typography variant="subtitle2">
                      {isLocal
                        ? t(
                            'firearmsReferenceTable.editFirearm.general.accordion.firearmReferenceLocalID.label',
                          )
                        : t(
                            'firearmsReferenceTable.editFirearm.general.accordion.firearmReferenceNo.label',
                          )}
                    </Typography>
                    <Typography variant="body2" sx={{ marginTop: '4px' }}>
                      {isLocal ? FRTDetails.frtId : FRTDetails.id}
                    </Typography>
                  </Grid>
                  <Grid size={{ xs: 12, md: 6, lg: 4, xl: 3 }}>
                    <Typography variant="subtitle2">
                      {t(
                        'firearmsReferenceTable.editFirearm.general.accordion.manufacturer.label',
                      )}
                    </Typography>
                    <Typography variant="body2" sx={{ marginTop: '4px' }}>
                      {findInDictionary(FRTDetails.manufacturerUniqueId, manufacturer)}
                    </Typography>
                  </Grid>
                  <Grid size={{ xs: 12, md: 6, lg: 4, xl: 3 }}>
                    <Typography variant="subtitle2">
                      {t(
                        'firearmsReferenceTable.editFirearm.general.accordion.countryOfManufacturer.label',
                      )}
                    </Typography>
                    <Typography variant="body2" sx={{ marginTop: '4px' }}>
                      {findInDictionary(FRTDetails.countryUniqueId, manufacturerCountry)}
                    </Typography>
                  </Grid>
                  <Grid size={{ xs: 12, md: 6, lg: 4, xl: 3 }}>
                    <Typography variant="subtitle2">
                      {t(
                        'firearmsReferenceTable.editFirearm.general.accordion.productCode.label',
                      )}
                    </Typography>
                    <Typography variant="body2" sx={{ marginTop: '4px' }}>
                      {FRTDetails.productCodes.length > 0
                        ? FRTDetails.productCodes.join(', ')
                        : '-'}
                    </Typography>
                  </Grid>
                </StyledDetailsWrapper>
              </Box>
            )}
          </Accordion>
        </Grid>
        <Grid size={{ xs: 12 }}>
          <Accordion
            title={t(
              'firearmsReferenceTable.firearmDetails.calibreShotsAndBarrelLength.accordion.name',
            )}
            expanded={true}
            name="calibre"
          >
            <Box sx={{ mb: 2 }}>
              {isFRTSpecificationsLoading ? (
                <Loader isLoading={isFRTSpecificationsLoading} />
              ) : (
                <div>
                  <Grid container columnSpacing={1} sx={{ my: 2, justifyContent: 'flex-end' }}>
                    <Grid
                      size={{ xs: 12 }}
                      sx={{ display: 'flex', justifyContent: 'flex-end' }}
                    >
                      {isLocal && hasPermissions([EPermission.MANAGE_FRT_FIREARM]) && (
                        <Button
                          label={t(
                            'firearmsReferenceTable.firearmDetails.calibreShotsAndBarrelLength.accordion.addCalibre.button',
                          )}
                          icon={EIconTypes.plus}
                          variant={EButtonVariants.contained}
                          onClick={() =>
                            FRTDetails
                              ? navigate(
                                  ERouteLinks.FRTAddCalibre.replace(':frtId', FRTDetails.id),
                                )
                              : {}
                          }
                        />
                      )}
                    </Grid>
                  </Grid>
                  {isDesktop ? (
                    <Table
                      columns={FRTSpecificationsTableColumns({
                        handleSelect: (specificationId: string) =>
                          navigate(
                            `${ERouteLinks.FRTEditCalibre.replace(':frtId', frtId).replace(
                              ':specificationId',
                              specificationId,
                            )}`,
                          ),
                        isLocal,
                        hasEditButton: true,
                        t,
                        hasPermissionsManageFRT: hasPermissions([
                          EPermission.MANAGE_FRT_FIREARM,
                        ]),
                      })}
                      dataSource={mapFRTSpecificationToDataSource(
                        FRTSpecifications,
                        barrelType,
                        caliber,
                      )}
                    />
                  ) : (
                    <CardList
                      items={mapFRTSpecificationToDataSource(
                        FRTSpecifications,
                        barrelType,
                        caliber,
                      )}
                      render={(data, index) => (
                        <FRTSpecificationsCard
                          data={data}
                          key={index}
                          isLocal={isLocal}
                          handleEdit={(specificationId: string) =>
                            navigate(
                              `${ERouteLinks.FRTEditCalibre.replace(':frtId', frtId).replace(
                                ':specificationId',
                                specificationId,
                              )}`,
                            )
                          }
                        />
                      )}
                    />
                  )}
                </div>
              )}
            </Box>
          </Accordion>
        </Grid>

        <Grid size={{ xs: 12 }}>
          <Accordion
            title={t('firearmsReferenceTable.editFirearm.notes.accordion.name')}
            expanded={firearmDetailsAllExpanded}
          >
            <div>
              {isFRTSpecificationsLoading ? (
                <Loader isLoading={isFRTSpecificationsLoading} />
              ) : (
                <StyledDetailsWrapper container sx={{ rowGap: 3 }}>
                  {frtNotes}
                </StyledDetailsWrapper>
              )}
            </div>
          </Accordion>
        </Grid>
      </Grid>
    </div>
  );
};

export { FRTDetailsPage };
